import { useState, useEffect, useCallback, useRef } from 'react';
import localforage from 'localforage';

const QUEUE_STORE = 'offline-queue';
const MAX_QUEUE_SIZE = 100;

// Configure localforage for queue storage
localforage.config({
  name: 'gm-enrollment-report',
  storeName: 'queue',
  description: 'Offline mutation queue for PWA',
});

/**
 * Hook for managing offline mutation queue
 * Queues API mutations when offline and syncs when back online
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onSync - Callback when syncing queued items
 * @param {Function} options.onError - Error handler
 * @returns {Object} Queue management
 *   - queue: array - Current queue items
 *   - queueSize: number - Number of items in queue
 *   - addToQueue: function - Add mutation to queue
 *   - clearQueue: function - Clear all queued items
 *   - syncQueue: function - Sync queued items (when online)
 *   - removeFromQueue: function - Remove specific item from queue
 */
export default function useOfflineQueue(options = {}) {
  const { onSync, onError } = options;
  const [queue, setQueue] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const isOnlineRef = useRef(navigator.onLine);

  // Load queue from storage on mount
  useEffect(() => {
    const loadQueue = async () => {
      try {
        const stored = await localforage.getItem(QUEUE_STORE);
        if (Array.isArray(stored)) {
          setQueue(stored);
        }
      } catch (err) {
        console.error('[OfflineQueue] Failed to load queue:', err);
        onError?.(err);
      }
    };
    loadQueue();
  }, [onError]);

  // Save queue to storage whenever it changes
  useEffect(() => {
    const saveQueue = async () => {
      try {
        await localforage.setItem(QUEUE_STORE, queue);
      } catch (err) {
        console.error('[OfflineQueue] Failed to save queue:', err);
        onError?.(err);
      }
    };
    if (queue.length > 0) {
      saveQueue();
    }
  }, [queue, onError]);

  // Monitor online status and auto-sync when back online
  useEffect(() => {
    const handleOnline = async () => {
      if (!isOnlineRef.current && queue.length > 0) {
        isOnlineRef.current = true;
        // Call syncQueue directly - it's stable and doesn't need to be in deps
        if (navigator.onLine && !isSyncing && queue.length > 0) {
          setIsSyncing(true);
          const itemsToSync = [...queue];
          try {
            if (onSync) {
              for (const item of itemsToSync) {
                try {
                  await onSync(item);
                  setQueue((prev) => prev.filter((qItem) => qItem.id !== item.id));
                } catch (err) {
                  console.error('[OfflineQueue] Failed to sync item:', err);
                }
              }
            }
          } finally {
            setIsSyncing(false);
          }
        }
      }
      isOnlineRef.current = true;
    };

    const handleOffline = () => {
      isOnlineRef.current = false;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [queue, isSyncing, onSync]);

  const addToQueue = useCallback((mutation) => {
    if (!navigator.onLine) {
      setQueue((prev) => {
        const next = [...prev, { ...mutation, queuedAt: new Date().toISOString(), id: Date.now() }];
        // Limit queue size
        return next.slice(-MAX_QUEUE_SIZE);
      });
      return { success: true, queued: true };
    }
    return { success: false, queued: false, reason: 'Online' };
  }, []);

  const removeFromQueue = useCallback((id) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearQueue = useCallback(async () => {
    setQueue([]);
    try {
      await localforage.removeItem(QUEUE_STORE);
    } catch (err) {
      console.error('[OfflineQueue] Failed to clear queue:', err);
      onError?.(err);
    }
  }, [onError]);

  const syncQueue = useCallback(async () => {
    if (!navigator.onLine || isSyncing || queue.length === 0) {
      return { success: false, synced: 0, reason: 'Cannot sync' };
    }

    setIsSyncing(true);
    const itemsToSync = [...queue];
    let synced = 0;
    const errors = [];

    try {
      if (onSync) {
        for (const item of itemsToSync) {
          try {
            await onSync(item);
            synced++;
            removeFromQueue(item.id);
          } catch (err) {
            errors.push({ item, error: err });
            console.error('[OfflineQueue] Failed to sync item:', err);
          }
        }
      }

      return { success: true, synced, errors, total: itemsToSync.length };
    } catch (err) {
      onError?.(err);
      return { success: false, synced, error: err.message };
    } finally {
      setIsSyncing(false);
    }
  }, [queue, isSyncing, onSync, removeFromQueue, onError]);

  return {
    queue,
    queueSize: queue.length,
    isSyncing,
    addToQueue,
    removeFromQueue,
    clearQueue,
    syncQueue,
  };
}

